import React, {useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {Badge, Button, Card, Grid, Image, message, Space, Tag, Typography} from 'antd';
import {ClockCircleOutlined, TagsOutlined, UpOutlined} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {useAuth} from '../../contexts/AuthContext';
import ReadLaterService from '../../service/readLaterService';
import {USE_USER} from '../../config/features';
import {sanitizeHtml} from '../../utils/htmlUtils';
import {parseTags, prioritizeTags} from '../../utils/tagUtils';
import {NewsCardData} from '../../types/cardData';
import {useTheme} from '../ThemeProvider';

dayjs.extend(relativeTime);
const { useBreakpoint } = Grid;
const { Text, Title } = Typography;

interface NewsItemProps {
    data: NewsCardData;
    showClusterInfo?: boolean;
}

const NewsItemCard: React.FC<NewsItemProps> = ({ data, showClusterInfo = true }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const screens = useBreakpoint();
    const { token } = useAuth();
    const { isDark } = useTheme();
    const isMobile = !screens.md;

    const [isInReadLater, setIsInReadLater] = useState<boolean>(data.isInReadLater || false);
    const [readLaterLoading, setReadLaterLoading] = useState<boolean>(false);
    const [tagsExpanded, setTagsExpanded] = useState<boolean>(false);
    const [imageError, setImageError] = useState(false);

    const timeSincePublication = data.publicationDate
        ? dayjs(data.publicationDate).fromNow(true)
        : 'N/A';

    // Parse and prioritize tags
    const allTags = prioritizeTags(parseTags(data.tagsJson));
    const maxVisibleTags = isMobile ? 2 : 3;
    const visibleTags = tagsExpanded ? allTags : allTags.slice(0, maxVisibleTags);
    const hasMoreTags = allTags.length > maxVisibleTags;

    const hasImage = !!data.imageUrl;
    const truncationLimit = hasImage ? (isMobile ? 80 : 120) : (isMobile ? 150 : 200);
    const truncatedContent = data.content
        ? data.content.length > truncationLimit
            ? data.content.substring(0, truncationLimit) + '...'
            : data.content
        : 'No content available.';

    const displayRating = data.breakingRating ?? 0;

    const relatedCount = data.articleCount && data.articleCount > 1
        ? data.articleCount
        : null;

    useEffect(() => {
        setIsInReadLater(data.isInReadLater || false);
    }, [data.isInReadLater]);

    const handleReadLaterClick = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!token) {
            message.warning('Please log in to save articles for later reading');
            return;
        }
        setReadLaterLoading(true);
        try {
            if (isInReadLater) {
                await ReadLaterService.removeFromReadLater(data.id);
                setIsInReadLater(false);
                message.success('Removed from read later');
            } else {
                await ReadLaterService.addToReadLater({
                    article_id: data.id,
                    cluster_id: data.clusterId,
                });
                setIsInReadLater(true);
                message.success('Added to read later');
            }
        } catch (error: any) {
            if (error.message && error.message.includes('already in read later')) {
                setIsInReadLater(true);
                message.info('Article is already in your read later list');
            } else {
                message.error(error.message || 'Failed to update read later');
            }
        } finally {
            setReadLaterLoading(false);
        }
    };

    const handleClick = () => {
        if (data.articleCount != null && data.articleCount <= 1 && data.articleUrl) {
            window.open(data.articleUrl, '_blank', 'noopener,noreferrer');
        } else if (data.clusterId) {
            navigate(`/news/cluster/${data.clusterId}`, {
                state: { from: location.pathname }
            });
        }
    };

    return (
        <Card
            className="news-item-card"
            hoverable
            styles={{ body: { padding: isMobile ? '12px' : '20px' } }}
            style={{
                marginBottom: '16px',
                cursor: 'pointer',
                borderRadius: '12px',
                overflow: 'hidden',
                background: isDark ? '#1a1a1a' : '#fff',
                borderColor: isDark ? '#303030' : '#e8e8e8',
                boxShadow: isDark ? 'none' : undefined,
            }}
            onClick={handleClick}
        >
            <div style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? '12px' : '16px'
            }}>
                {/* Image */}
                {hasImage && !imageError && (
                    <div style={{
                        flexShrink: 0,
                        width: isMobile ? '100%' : '200px',
                        height: isMobile ? '200px' : '140px',
                    }}>
                        <Image
                            src={data.imageUrl ?? undefined}
                            alt={data.headline}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                borderRadius: '8px',
                                border: '1px solid #f0f0f0'
                            }}
                            fallback="https://via.placeholder.com/200x140?text=No+Image"
                            onError={() => setImageError(true)}
                            preview={false}
                        />
                    </div>
                )}

                <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Line 1: Rating, Website, Cluster Size, Time */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '6px',
                        gap: '8px',
                        flexWrap: 'wrap',
                    }}>
                        <Space size={8} align="center" wrap>
                            <Tag
                                color="orange"
                                style={{
                                    margin: 0,
                                    fontSize: '12px',
                                    fontWeight: 'bold',
                                    padding: '1px 8px',
                                }}
                            >
                                {displayRating}/5
                            </Tag>
                            <Text type="secondary" style={{ fontSize: '13px' }}>
                                {data.websiteName || 'Unknown'}
                            </Text>
                            {showClusterInfo && relatedCount && (
                                <Badge
                                    count={relatedCount}
                                    style={{
                                        backgroundColor: '#1890ff',
                                        fontSize: '11px',
                                        padding: '0 6px',
                                    }}
                                />
                            )}
                        </Space>
                        <Space size={8} align="center">
                            <Text type="secondary" style={{ fontSize: '13px' }}>
                                {timeSincePublication}
                            </Text>
                            {USE_USER && token && (
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<ClockCircleOutlined />}
                                    loading={readLaterLoading}
                                    style={{
                                        color: isInReadLater ? '#52c41a' : '#8c8c8c',
                                        padding: '4px 8px',
                                        height: 'auto',
                                        fontSize: '12px'
                                    }}
                                    onClick={handleReadLaterClick}
                                >
                                    {isInReadLater ? 'Saved' : 'Read Later'}
                                </Button>
                            )}
                        </Space>
                    </div>

                    {/* Line 2: Headline + Content */}
                    <Title
                        level={4}
                        style={{
                            margin: '0 0 6px 0',
                            fontWeight: 'bold',
                            lineHeight: '1.3',
                            fontSize: isMobile ? '16px' : '18px',
                        }}
                        ellipsis={{ rows: 2, tooltip: data.headline }}
                    >
                        {data.headline}
                    </Title>

                    <div
                        className="article-content"
                        style={{
                            margin: '0 0 10px 0',
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                        }}
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(truncatedContent) }}
                    />

                    {/* Line 3: Tags + Recent Updates */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: '6px',
                    }}>
                        {allTags.length > 0 && (
                            <Space wrap size={[4, 6]}>
                                {visibleTags.map((tag, index) => (
                                    <Tag
                                        key={index}
                                        color="blue"
                                        style={{
                                            margin: 0,
                                            fontSize: '12px',
                                            padding: '2px 8px',
                                            cursor: 'pointer'
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(`/tags?tag=${encodeURIComponent(tag.name)}`);
                                        }}
                                    >
                                        #{tag.name}
                                    </Tag>
                                ))}
                                {hasMoreTags && (
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={tagsExpanded ? <UpOutlined /> : <TagsOutlined />}
                                        style={{
                                            fontSize: '11px',
                                            padding: '0 6px',
                                            height: '24px',
                                            minWidth: 'auto',
                                            color: '#1890ff',
                                            border: '1px solid #d9d9d9',
                                            borderRadius: '4px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '2px'
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setTagsExpanded(!tagsExpanded);
                                        }}
                                        title={tagsExpanded ? 'Show less tags' : `Show ${allTags.length - maxVisibleTags} more tags`}
                                    >
                                        {tagsExpanded ? 'Less' : `+${allTags.length - maxVisibleTags}`}
                                    </Button>
                                )}
                            </Space>
                        )}
                        {showClusterInfo && data.recentArticleCount != null && data.recentArticleCount > 0 && (
                            <Tag color="green" style={{ margin: 0 }}>
                                {data.recentArticleCount} recent updates
                            </Tag>
                        )}
                    </div>
                </div>
            </div>
        </Card>
    );
};

export default NewsItemCard;
